if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton =
          this.querySelector('[type="submit"]');

        this.submitButtonText =
          this.submitButton.querySelector('span');

        /*
        ========================================
        TOYFIZZ GIFT WRAP
        ========================================
        */

        this.giftWrapCheckbox =
          this.querySelector('[data-gift-wrap-checkbox]');

        this.dynamicCheckoutWrapper =
          this.querySelector('[data-dynamic-checkout-wrapper]');

        this.giftWrapCheckoutNote =
          this.querySelector('[data-gift-wrap-checkout-note]');

        if (this.giftWrapCheckbox) {
          this.giftWrapCheckbox.addEventListener(
            'change',
            this.onGiftWrapChange.bind(this)
          );

          this.onGiftWrapChange();
        }

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute(
            'aria-haspopup',
            'dialog'
          );
        }

        this.hideErrors =
          this.dataset.hideErrors === 'true';
      }


      /*
      ========================================
      GIFT WRAP CHECKBOX
      ========================================
      */

      onGiftWrapChange() {
        if (!this.giftWrapCheckbox) return;

        const giftWrapSelected =
          this.giftWrapCheckbox.checked;

        /*
        Hide Buy It Now while Gift Wrap
        is selected because accelerated
        checkout bypasses Add to Cart.
        */

        if (this.dynamicCheckoutWrapper) {
          this.dynamicCheckoutWrapper.hidden =
            giftWrapSelected;
        }

        if (this.giftWrapCheckoutNote) {
          this.giftWrapCheckoutNote.hidden =
            !giftWrapSelected;
        }
      }


      /*
      ========================================
      EXISTING LINE ITEM PROPERTIES
      ========================================
      */

      getLineItemProperties(formData) {
        const properties = {};

        for (const [name, value] of formData.entries()) {
          const match =
            name.match(/^properties\[(.*)\]$/);

          if (!match || value === '') continue;

          properties[match[1]] = value;
        }

        return properties;
      }


      /*
      ========================================
      BUILD TOY + GIFT WRAP REQUEST
      ========================================
      */

      buildGiftWrapPayload(formData) {
        const variantId =
          formData.get('id');

        const quantityValue =
          parseInt(
            formData.get('quantity') || '1',
            10
          );

        const quantity =
          Number.isFinite(quantityValue) &&
          quantityValue > 0
            ? quantityValue
            : 1;


        /*
        Main toy
        */

        const mainItem = {
          id: Number(variantId),
          quantity: quantity
        };


        /*
        Preserve any existing product
        line item properties.
        */

        const properties =
          this.getLineItemProperties(formData);

        if (Object.keys(properties).length) {
          mainItem.properties =
            properties;
        }


        /*
        Preserve selling plans if present.
        */

        const sellingPlan =
          formData.get('selling_plan');

        if (sellingPlan) {
          mainItem.selling_plan =
            Number(sellingPlan);
        }


        const items = [mainItem];


        /*
        Gift Wrap product
        */

        const giftWrapVariantId =
          Number(
            this.giftWrapCheckbox?.dataset
              .giftWrapVariantId
          );


        if (giftWrapVariantId) {
          items.push({
            id: giftWrapVariantId,

            /*
            Quantity matches toy quantity.
            2 toys = 2 gift wraps.
            */

            quantity: quantity,

            properties: {
              'Gift wrap for':
                this.giftWrapCheckbox?.dataset
                  .giftWrapFor ||
                'Gift item'
            }
          });
        }


        const payload = {
          items: items
        };


        /*
        Ask Shopify to return updated
        cart drawer/cart notification HTML.
        */

        if (this.cart) {
          payload.sections =
            this.cart
              .getSectionsToRender()
              .map(
                (section) => section.id
              );

          payload.sections_url =
            window.location.pathname;

          this.cart.setActiveElement(
            document.activeElement
          );
        }


        return payload;
      }


      /*
      ========================================
      FIX SHOPIFY MULTI-ITEM RESPONSE
      ========================================

      Multiple-item /cart/add.js responses
      contain:

      response.items[]

      Studio's cart notification/drawer may
      expect:

      response.id
      response.key

      We copy those values from the main toy
      so Studio can render normally.
      */

      normalizeMultiAddResponse(
        response,
        productVariantId
      ) {
        if (
          !response ||
          !Array.isArray(response.items) ||
          response.items.length === 0
        ) {
          return response;
        }


        const mainItem =
          response.items.find(
            (item) =>
              String(
                item.variant_id ||
                item.id
              ) ===
              String(productVariantId)
          ) ||
          response.items[0];


        if (!mainItem) {
          return response;
        }


        return {
          ...response,

          id:
            response.id ||
            mainItem.id ||
            mainItem.variant_id,

          key:
            response.key ||
            mainItem.key
        };
      }


      /*
      ========================================
      PUBLISH CART UPDATE SAFELY
      ========================================

      A cart UI/subscriber problem must not
      tell the customer that Add to Cart
      failed after Shopify already added it.
      */

      safelyPublishCartUpdate(
        cartResponse,
        productVariantId
      ) {
        const startMarker =
          CartPerformance
            .createStartingMarker(
              'add:wait-for-subscribers'
            );

        try {
          const result =
            publish(
              PUB_SUB_EVENTS.cartUpdate,
              {
                source:
                  'product-form',

                productVariantId:
                  productVariantId,

                cartData:
                  cartResponse
              }
            );


          Promise.resolve(result)
            .then(() => {
              CartPerformance
                .measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
            })
            .catch((error) => {
              console.warn(
                'ToyFizz cart subscriber warning:',
                error
              );

              CartPerformance
                .measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
            });

        } catch (error) {
          console.warn(
            'ToyFizz cart subscriber warning:',
            error
          );

          CartPerformance
            .measureFromMarker(
              'add:wait-for-subscribers',
              startMarker
            );
        }
      }


      /*
      ========================================
      RENDER CART SAFELY
      ========================================
      */

      safelyRenderCart(cartResponse) {
        try {
          this.cart.renderContents(
            cartResponse
          );
        } catch (error) {
          console.error(
            'ToyFizz cart display warning:',
            error
          );

          /*
          Important:
          The products have already been
          successfully added by Shopify.

          Therefore DO NOT show a false
          "Something went wrong" message.
          */

          if (
            this.cart &&
            this.cart.classList.contains(
              'is-empty'
            )
          ) {
            this.cart.classList.remove(
              'is-empty'
            );
          }
        }
      }


      /*
      ========================================
      ADD TO CART
      ========================================
      */

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.submitButton.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }


        /*
        Remove any old error message.
        */

        this.handleErrorMessage();


        this.submitButton.setAttribute(
          'aria-disabled',
          true
        );

        this.submitButton.classList.add(
          'loading'
        );

        this
          .querySelector(
            '.loading__spinner'
          )
          .classList.remove(
            'hidden'
          );


        const config =
          fetchConfig('javascript');

        config.headers[
          'X-Requested-With'
        ] = 'XMLHttpRequest';


        const formData =
          new FormData(this.form);


        const productVariantId =
          formData.get('id');


        /*
        CRITICAL FIX:

        Only use the multiple-item request
        when Gift Wrap is ACTUALLY checked.

        Otherwise use Studio's original
        product form request.
        */

        const giftWrapSelected =
          Boolean(
            this.giftWrapCheckbox?.checked
          );


        /*
        ========================================
        GIFT WRAP SELECTED
        ========================================
        */

        if (giftWrapSelected) {
          config.headers[
            'Content-Type'
          ] = 'application/json';

          config.body =
            JSON.stringify(
              this.buildGiftWrapPayload(
                formData
              )
            );
        }


        /*
        ========================================
        NORMAL ADD TO CART
        ========================================
        */

        else {
          delete config.headers[
            'Content-Type'
          ];


          if (this.cart) {
            formData.append(
              'sections',

              this.cart
                .getSectionsToRender()
                .map(
                  (section) =>
                    section.id
                )
            );


            formData.append(
              'sections_url',
              window.location.pathname
            );


            this.cart.setActiveElement(
              document.activeElement
            );
          }


          config.body =
            formData;
        }


        /*
        ========================================
        SEND REQUEST
        ========================================
        */

        fetch(
          `${routes.cart_add_url}`,
          config
        )

          .then(
            (response) =>
              response.json()
          )


          .then((response) => {


            /*
            ========================================
            REAL SHOPIFY CART ERROR
            ========================================
            */

            if (response.status) {
              publish(
                PUB_SUB_EVENTS.cartError,
                {
                  source:
                    'product-form',

                  productVariantId:
                    productVariantId,

                  errors:
                    response.errors ||
                    response.description,

                  message:
                    response.message
                }
              );


              this.handleErrorMessage(
                response.description
              );


              const soldOutMessage =
                this.submitButton
                  .querySelector(
                    '.sold-out-message'
                  );


              if (!soldOutMessage) {
                this.error = true;
                return;
              }


              this.submitButton
                .setAttribute(
                  'aria-disabled',
                  true
                );


              this.submitButtonText
                .classList.add(
                  'hidden'
                );


              soldOutMessage
                .classList.remove(
                  'hidden'
                );


              this.error = true;

              return;
            }


            /*
            No drawer/notification:
            redirect to cart.
            */

            if (!this.cart) {
              window.location =
                window.routes.cart_url;

              return;
            }


            /*
            ========================================
            NORMALIZE MULTI-ADD RESPONSE
            ========================================
            */

            const cartResponse =
              giftWrapSelected
                ? this.normalizeMultiAddResponse(
                    response,
                    productVariantId
                  )
                : response;


            /*
            Shopify has successfully added
            the product(s).
            */

            this.error = false;

            this.handleErrorMessage();


            /*
            Update theme components.
            */

            this.safelyPublishCartUpdate(
              cartResponse,
              productVariantId
            );


            /*
            ========================================
            QUICK ADD MODAL SUPPORT
            ========================================
            */

            const quickAddModal =
              this.closest(
                'quick-add-modal'
              );


            if (quickAddModal) {
              document.body
                .addEventListener(
                  'modalClosed',

                  () => {
                    setTimeout(() => {
                      CartPerformance
                        .measure(
                          'add:paint-updated-sections',

                          () => {
                            this.safelyRenderCart(
                              cartResponse
                            );
                          }
                        );
                    });
                  },

                  {
                    once: true
                  }
                );


              quickAddModal.hide(
                true
              );
            }


            /*
            ========================================
            NORMAL PRODUCT PAGE
            ========================================
            */

            else {
              CartPerformance.measure(
                'add:paint-updated-sections',

                () => {
                  this.safelyRenderCart(
                    cartResponse
                  );
                }
              );
            }
          })


          /*
          ========================================
          ACTUAL NETWORK / API FAILURE ONLY
          ========================================
          */

          .catch((error) => {
            console.error(
              'ToyFizz add-to-cart request failed:',
              error
            );


            this.handleErrorMessage(
              'Something went wrong. Please try again.'
            );


            this.error = true;
          })


          /*
          ========================================
          FINISH
          ========================================
          */

          .finally(() => {
            this.submitButton
              .classList.remove(
                'loading'
              );


            if (
              this.cart &&
              this.cart.classList.contains(
                'is-empty'
              )
            ) {
              this.cart.classList.remove(
                'is-empty'
              );
            }


            if (!this.error) {
              this.submitButton
                .removeAttribute(
                  'aria-disabled'
                );
            }


            this
              .querySelector(
                '.loading__spinner'
              )
              .classList.add(
                'hidden'
              );


            CartPerformance
              .measureFromEvent(
                'add:user-action',
                evt
              );
          });
      }


      /*
      ========================================
      ERROR MESSAGE
      ========================================
      */

      handleErrorMessage(
        errorMessage = false
      ) {
        if (this.hideErrors) return;


        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );


        if (!this.errorMessageWrapper) {
          return;
        }


        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper
            .querySelector(
              '.product-form__error-message'
            );


        this.errorMessageWrapper
          .toggleAttribute(
            'hidden',
            !errorMessage
          );


        if (errorMessage) {
          this.errorMessage.textContent =
            errorMessage;
        }
      }


      /*
      ========================================
      ENABLE / DISABLE BUTTON
      ========================================
      */

      toggleSubmitButton(
        disable = true,
        text
      ) {
        if (disable) {
          this.submitButton
            .setAttribute(
              'disabled',
              'disabled'
            );


          if (text) {
            this.submitButtonText
              .textContent =
                text;
          }

        } else {
          this.submitButton
            .removeAttribute(
              'disabled'
            );


          this.submitButtonText
            .textContent =
              window.variantStrings
                .addToCart;
        }
      }


      /*
      ========================================
      VARIANT ID
      ========================================
      */

      get variantIdInput() {
        return this.form.querySelector(
          '[name=id]'
        );
      }
    }
  );
}